import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { 
  Plus, Edit2, Trash2, Settings, Check, X, Search, RefreshCw,
  Calendar, Clock, ArrowRight, Save, FileText, Briefcase
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LeaveConfiguration({ user, onLogout }) {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    leave_type: '',
    days_per_year: 12,
    carry_forward: false,
    max_carry_forward: 0,
    encashable: false,
    medical_certificate_required_after_days: 0,
    advance_notice_days: 2,
    half_day_allowed: true,
    max_consecutive_days: 0
  });

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const fetchLeaveTypes = async () => {
    setDataLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-config/leave-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveTypes(response.data.leave_types || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/hr-config/leave-types`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(editingType ? 'Leave type updated successfully!' : 'Leave type created successfully!');
      resetForm();
      fetchLeaveTypes();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (leaveType) => {
    if (!window.confirm(`Are you sure you want to delete "${leaveType}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hr-config/leave-types/${encodeURIComponent(leaveType)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Leave type deleted successfully!');
      fetchLeaveTypes();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      leave_type: '',
      days_per_year: 12,
      carry_forward: false,
      max_carry_forward: 0,
      encashable: false,
      medical_certificate_required_after_days: 0,
      advance_notice_days: 2,
      half_day_allowed: true,
      max_consecutive_days: 0
    });
    setEditingType(null);
    setShowForm(false);
  };

  const handleEdit = (type) => {
    setFormData({
      leave_type: type.leave_type,
      days_per_year: type.days_per_year || 0,
      carry_forward: type.carry_forward || false,
      max_carry_forward: type.max_carry_forward || 0,
      encashable: type.encashable || false,
      medical_certificate_required_after_days: type.medical_certificate_required_after_days || 0,
      advance_notice_days: type.advance_notice_days || 2,
      half_day_allowed: type.half_day_allowed !== false,
      max_consecutive_days: type.max_consecutive_days || 0
    });
    setEditingType(type);
    setShowForm(true);
  };

  const filteredLeaveTypes = leaveTypes.filter(type =>
    type.leave_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalDays = leaveTypes.reduce((sum, lt) => sum + (lt.days_per_year || 0), 0);

  // Stat Card Component (Zobox style)
  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-xl" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  if (dataLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>Leave Configuration</h1>
            <p className="text-gray-500">Configure leave types and annual allocations</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchLeaveTypes}
              disabled={dataLoading}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${dataLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 text-white rounded-lg flex items-center gap-2"
              style={{ backgroundColor: '#2b3380' }}
            >
              <Plus className="w-4 h-4" />
              Add Leave Type
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Leave Types"
            value={leaveTypes.length}
            subtitle="Configured"
            icon={FileText}
            color="#2b3380"
          />
          <StatCard
            title="Total Days"
            value={totalDays}
            subtitle="Annual allocation"
            icon={Calendar}
            color="#4caf50"
          />
          <StatCard
            title="Carry Forward"
            value={leaveTypes.filter(lt => lt.carry_forward).length}
            subtitle="Enabled"
            icon={ArrowRight}
            color="#ff9800"
          />
          <StatCard
            title="Encashable"
            value={leaveTypes.filter(lt => lt.encashable).length}
            subtitle="Allowed"
            icon={Briefcase}
            color="#7e57c2"
          />
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          {/* Search Bar */}
          <div className="p-5 border-b border-gray-100">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search leave types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
              />
            </div>
          </div>

          {/* Leave Types Table */}
          <div className="p-5">
            {filteredLeaveTypes.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Settings className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No leave types configured</p>
                <p className="text-sm">Click "Add Leave Type" to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Leave Type</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Days/Year</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Carry Forward</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Encashable</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Half Day</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Notice Days</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeaveTypes.map((type, idx) => (
                      <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: '#2b3380' }}>
                              {type.leave_type?.charAt(0) || 'L'}
                            </div>
                            <span className="font-medium text-gray-800">{type.leave_type}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 font-medium text-sm">
                            {type.days_per_year} days
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {type.carry_forward ? (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700 flex items-center gap-1 w-fit">
                              <Check className="w-3 h-3" />
                              Yes (Max: {type.max_carry_forward || 0})
                            </span>
                          ) : (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-500 flex items-center gap-1 w-fit">
                              <X className="w-3 h-3" />
                              No
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {type.encashable ? (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">Yes</span>
                          ) : (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-500">No</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {type.half_day_allowed !== false ? (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700">Yes</span>
                          ) : (
                            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-500">No</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-gray-700">{type.advance_notice_days || 0} days</span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(type)}
                              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(type.leave_type)}
                              className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingType ? 'Edit Leave Type' : 'Add Leave Type'}
                </h2>
                <button onClick={resetForm} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto max-h-[60vh]">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type Name *</label>
                  <input
                    type="text"
                    value={formData.leave_type}
                    onChange={(e) => setFormData({...formData, leave_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                    placeholder="e.g., Sick Leave, Casual Leave"
                    required
                    disabled={editingType}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Days Per Year *</label>
                    <input
                      type="number"
                      value={formData.days_per_year}
                      onChange={(e) => setFormData({...formData, days_per_year: parseInt(e.target.value)})}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Carry Forward</label>
                    <input
                      type="number"
                      value={formData.max_carry_forward}
                      onChange={(e) => setFormData({...formData, max_carry_forward: parseInt(e.target.value)})}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                      min="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Advance Notice Days</label>
                    <input
                      type="number"
                      value={formData.advance_notice_days}
                      onChange={(e) => setFormData({...formData, advance_notice_days: parseInt(e.target.value)})}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medical Cert After (Days)</label>
                    <input
                      type="number"
                      value={formData.medical_certificate_required_after_days}
                      onChange={(e) => setFormData({...formData, medical_certificate_required_after_days: parseInt(e.target.value)})}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400 outline-none"
                      min="0"
                    />
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.carry_forward}
                      onChange={(e) => setFormData({...formData, carry_forward: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Allow Carry Forward</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.encashable}
                      onChange={(e) => setFormData({...formData, encashable: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Allow Encashment</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.half_day_allowed}
                      onChange={(e) => setFormData({...formData, half_day_allowed: e.target.checked})}
                      className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Allow Half Day</span>
                  </label>
                </div>
              </form>

              <div className="flex justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-5 py-2 text-white rounded-lg flex items-center gap-2"
                  style={{ backgroundColor: loading ? '#cccccc' : '#2b3380' }}
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Saving...' : editingType ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default LeaveConfiguration;
